import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import dayjs from 'dayjs';
import {
  DATE_ONLY_FORMAT,
  mapReservationCodeToName,
  MiddlewareBaseResponse,
} from '@ireckonu/shared-ui';

import { PersonLookupMappingService } from './person-lookup-mapping.service';
import { LOYALTY_LOOKUP_PARAMS } from '../constants';
import { ServiceUrlKey } from '../models/configuration.api';
import { PersonDataInterface } from '../models/person-lookup-api.interface';
import { ProfileApiInterface } from '../models/profiles.api';
import {
  Venue,
  VenueReservationDisplayItem,
  VenueReservationRequest,
  VenueReservationResponseItem,
  VenueSearchParams,
} from '../models/venues.api';
import { ConfigurationQueryService } from './configuration-query.service';
import { ProfilesService } from './profiles.service';

const PAGE_SIZE = 20;
const TIME_SLICE_LENGTH = 5;
const PRIMARY_GUEST_RELATION = 'PrimaryGuest';

@Injectable({ providedIn: 'root' })
export class VenuesSearchService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigurationQueryService);
  private readonly personLookupMapping = inject(PersonLookupMappingService);
  private readonly profilesService = inject(ProfilesService);

  private get venuesBaseUrl(): string {
    return this.configService.getServiceUrl(ServiceUrlKey.VenuesBaseUrl) ?? '';
  }

  private get lookUpBaseUrl(): string {
    return this.configService.getServiceUrl(ServiceUrlKey.LookUpBaseUrl) ?? '';
  }

  private venuesList$: Observable<Venue[]> | null = null;

  getVenuesList(): Observable<Venue[]> {
    if (!this.venuesList$) {
      this.venuesList$ = this.http
        .post<
          MiddlewareBaseResponse<Venue[]>
        >(`${this.venuesBaseUrl}/api/v1/venue/search`, {})
        .pipe(
          map((response) => response.Data ?? []),
          shareReplay(1),
        );
    }
    return this.venuesList$;
  }

  searchVenueReservations(
    query: VenueSearchParams,
    venues: Venue[],
    page: number,
    date?: Date,
  ): Observable<VenueReservationDisplayItem[]> {
    if (!this.hasSearchValues(query)) {
      return of([]);
    }

    return this.resolvePersonIds(query).pipe(
      switchMap((personIds) => {
        if (personIds !== undefined && personIds.length === 0) {
          return of([]);
        }
        return this.fetchReservations(query, personIds, page, date);
      }),
      switchMap((items) => this.enrichWithProfiles(items)),
      map((enrichedItems) =>
        enrichedItems.map((item) => this.mapToDisplayItem(item, venues)),
      ),
    );
  }

  private resolvePersonIds(
    query: VenueSearchParams,
  ): Observable<number[] | undefined> {
    const isProfileSearch =
      !!query.firstName || !!query.lastName || !!query.email || !!query.phone;

    if (!isProfileSearch) {
      return of(undefined);
    }

    const requestBody = this.personLookupMapping.mapSearchQueryToRequest(
      {
        firstName: query.firstName,
        lastName: query.lastName,
        email: query.email,
        phone: query.phone,
      },
      LOYALTY_LOOKUP_PARAMS,
    );

    return this.http
      .post<
        MiddlewareBaseResponse<PersonDataInterface[]>
      >(`${this.lookUpBaseUrl}/person/lookup`, requestBody)
      .pipe(map((response) => (response.Data ?? []).map((p) => p.PersonId)));
  }

  private fetchReservations(
    query: VenueSearchParams,
    personIds: number[] | undefined,
    page: number,
    date?: Date,
  ): Observable<VenueReservationResponseItem[]> {
    const requestModel: VenueReservationRequest = {
      IncludeExternalReferences: true,
      IncludeContacts: true,
      IncludePersonRelations: true,
      IncludeFolios: true,
      Enrichers: ['MwPerson'],
      Limit: PAGE_SIZE,
      Skip: page * PAGE_SIZE,
    };

    if (date) {
      const formattedDate = dayjs(date).format(DATE_ONLY_FORMAT);

      requestModel.DateRange = {
        Type: 'Arrival',
        Start: formattedDate,
        End: formattedDate,
      };
    }

    if (query.venueReservationId) {
      const parsed = Number(query.venueReservationId);
      if (Number.isFinite(parsed)) {
        requestModel.VenueReservationId = parsed;
      }
    }

    if (query.venueReservationNumber) {
      requestModel.ExternalReference = { Code: query.venueReservationNumber };
    }

    if (query.venueCodes) {
      requestModel.VenueCodes = query.venueCodes;
    }

    if (personIds) {
      requestModel.PersonRelation = { PersonIds: personIds };
    }

    return this.http
      .post<
        MiddlewareBaseResponse<VenueReservationResponseItem[]>
      >(`${this.venuesBaseUrl}/api/v1/venue/reservation/search`, requestModel)
      .pipe(map((response) => response.Data ?? []));
  }

  private enrichWithProfiles(
    items: VenueReservationResponseItem[],
  ): Observable<
    { item: VenueReservationResponseItem; profile?: ProfileApiInterface }[]
  > {
    if (items.length === 0) {
      return of([]);
    }

    const enrichedPersonIds = items
      .flatMap((item) => item.PersonRelations ?? [])
      .filter((rel) => rel.RelationTypeCode === PRIMARY_GUEST_RELATION)
      .map((rel) => rel.PersonId);

    const uniquePersonIds = [...new Set(enrichedPersonIds)];

    return this.fetchProfilesByPersonIds(uniquePersonIds).pipe(
      map((profileMap) =>
        items.map((item) => {
          const personId = item.PersonRelations?.find(
            (rel) => rel.RelationTypeCode === PRIMARY_GUEST_RELATION,
          )?.PersonId;
          const profile = personId ? profileMap.get(personId) : undefined;
          return { item, profile };
        }),
      ),
    );
  }

  private fetchProfilesByPersonIds(
    personIds: number[],
  ): Observable<Map<number, ProfileApiInterface>> {
    if (personIds.length === 0) {
      return of(new Map<number, ProfileApiInterface>());
    }

    return this.profilesService.getProfiles({ personIds }).pipe(
      take(1),
      map((profiles) => {
        const profileMap = new Map<number, ProfileApiInterface>();
        profiles.forEach((profile) => {
          profileMap.set(Number(profile.id), profile);
        });
        return profileMap;
      }),
    );
  }

  private hasSearchValues(query: VenueSearchParams): boolean {
    return (
      !!query.venueReservationId ||
      !!query.venueReservationNumber ||
      !!query.firstName ||
      !!query.lastName ||
      !!query.email ||
      !!query.phone ||
      !!query.venueCodes?.length
    );
  }

  private mapToDisplayItem(
    {
      item,
      profile,
    }: { item: VenueReservationResponseItem; profile?: ProfileApiInterface },
    venues: Venue[],
  ): VenueReservationDisplayItem {
    const venue = venues.find((v) => v.Code === item.VenueCode);

    let startDate: string | undefined;
    let timeStart = '';
    let timeEnd = '';

    if (item.StartDateTime) {
      startDate = item.StartDateTime;
      timeStart = new Date(item.StartDateTime)
        .toTimeString()
        .slice(0, TIME_SLICE_LENGTH);
    } else if (item.StartDate) {
      startDate = item.StartDate;
      timeStart = item.StartTime?.slice(0, TIME_SLICE_LENGTH) ?? '';
    }

    if (item.EndDateTime) {
      timeEnd = new Date(item.EndDateTime)
        .toTimeString()
        .slice(0, TIME_SLICE_LENGTH);
    } else if (item.EndTime) {
      timeEnd = item.EndTime.slice(0, TIME_SLICE_LENGTH);
    }

    return {
      id: item.Id,
      profileDetails: profile,
      venueName: venue?.Name ?? item.VenueCode,
      venueReservationNumber: item.ExternalReferences?.[0]?.Code,
      venueCode: item.VenueCode,
      status: mapReservationCodeToName(item.StatusCode ?? item.Status ?? ''),
      date: startDate ?? '',
      timeStart,
      timeEnd,
      pax: item.Pax ?? item.NumberOfAdults,
    };
  }
}
